import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  FileSpreadsheet, Plus, Pencil, Trash2, Check, X, GripVertical, Calculator,
  Ship, Globe2, Building2, ArrowRight, Info, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import FormulaBuilder from '../components/FormulaBuilder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Pricing Models ───────────────────────────────────────────────────────────

const pricingModels = [
  {
    id: 'cost-plus',
    name: 'Cost Plus Pricing',
    shortName: 'Cost Plus',
    icon: Calculator,
    color: 'emerald',
    description: 'Calculate selling price by applying a margin percentage to the total cost.',
    usage: 'Common for both domestic and export pricing.',
    formula: 'Selling Price = Total Cost / (1 - Margin %)',
    details: [
      'Includes inbound logistics, overheads, duties, packaging in Total Cost',
      'Margin % applied on selling price (not markup on cost)',
      'Same formula pattern as DBP = COGS / (1 - GP%)',
      'Ensures consistent margin regardless of cost fluctuations'
    ],
    fields: [
      { name: 'Purchase Price', key: 'purchase_price' },
      { name: 'ISC %', key: 'isc_percent' },
      { name: 'Customs Duty %', key: 'customs_duty_percent' },
      { name: 'Margin %', key: 'margin_percent' },
    ],
    status: 'active'
  },
  {
    id: 'fob',
    name: 'FOB (Free on Board) Pricing',
    shortName: 'FOB',
    icon: Ship,
    color: 'blue',
    description: 'Price determined until the point goods are loaded for shipping. Used for export markets.',
    usage: 'Export pricing with cost-plus method up to FOB point.',
    formula: 'FOB Price = COGS × (1 + Markup %) → Convert to Foreign Currency',
    details: [
      'Price includes costs until goods loaded on ship',
      'Typically cost-plus method until FOB point',
      'Final price converted to foreign currency',
      'Exchange rate applied at specified rate'
    ],
    fields: [
      { name: 'Purchase Price', key: 'purchase_price' },
      { name: 'ISC %', key: 'isc_percent' },
      { name: 'FOB Markup %', key: 'fob_markup_percent' },
      { name: 'Target Currency', key: 'target_currency' },
      { name: 'Exchange Rate', key: 'exchange_rate' },
    ],
    status: 'coming_soon'
  },
  {
    id: 'itp',
    name: 'ITP (International Transfer Pricing)',
    shortName: 'ITP',
    icon: Building2,
    color: 'purple',
    description: 'Price for parts sold between related companies in different countries.',
    usage: 'Intercompany transactions with specific transfer pricing rules.',
    formula: 'ITP = Cost-Plus up to DBP + Intercompany Adjustments',
    details: [
      'Used for intercompany part transfers',
      'Similar to cost-plus up to DBP level',
      'Specific rules per intercompany agreements',
      'Must comply with transfer pricing regulations'
    ],
    fields: [
      { name: 'Purchase Price', key: 'purchase_price' },
      { name: 'ISC %', key: 'isc_percent' },
      { name: 'GP %', key: 'gp_percent' },
      { name: 'Transfer Adjustment %', key: 'transfer_adjustment' },
      { name: 'Related Entity', key: 'related_entity' },
    ],
    status: 'coming_soon'
  },
  {
    id: 'glp',
    name: 'GLP (Global List Pricing)',
    shortName: 'GLP',
    icon: Globe2,
    color: 'amber',
    description: 'Pricing dictated by global HQ, often in home currency (e.g., Japanese Yen).',
    usage: 'Central office provides list price and discount; local unit converts and validates GP%.',
    formula: 'Local Price = (GLP - Discount) × Exchange Rate + Local Taxes',
    details: [
      'Global HQ sets list price in home currency',
      'Discount percentage provided by central office',
      'Local unit converts to local currency',
      'Local taxes applied, GP% validated'
    ],
    fields: [
      { name: 'Global List Price', key: 'global_list_price' },
      { name: 'Home Currency', key: 'home_currency' },
      { name: 'Discount %', key: 'discount_percent' },
      { name: 'Exchange Rate', key: 'exchange_rate' },
      { name: 'Local Tax %', key: 'local_tax_percent' },
    ],
    status: 'coming_soon'
  }
];

const getColorClasses = (color) => {
  const colors = {
    emerald: {
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/30',
      text: 'text-emerald-500',
      icon: 'text-emerald-400',
      badge: 'bg-emerald-500/20 text-emerald-400',
      button: 'bg-emerald-600 hover:bg-emerald-700'
    },
    blue: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      text: 'text-blue-500',
      icon: 'text-blue-400',
      badge: 'bg-blue-500/20 text-blue-400',
      button: 'bg-blue-600 hover:bg-blue-700'
    },
    purple: {
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
      text: 'text-purple-500',
      icon: 'text-purple-400',
      badge: 'bg-purple-500/20 text-purple-400',
      button: 'bg-purple-600 hover:bg-purple-700'
    },
    amber: {
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      text: 'text-amber-500',
      icon: 'text-amber-400',
      badge: 'bg-amber-500/20 text-amber-400',
      button: 'bg-amber-600 hover:bg-amber-700'
    }
  };
  return colors[color] || colors.emerald;
};

function PricingModelCard({ model, onSelect }) {
  const colors = getColorClasses(model.color);
  const Icon = model.icon;

  return (
    <div className={`bg-white dark:bg-zinc-900 border ${colors.border} rounded-xl p-6 hover:shadow-lg transition-all duration-300`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-xl ${colors.bg}`}>
          <Icon className={`w-6 h-6 ${colors.icon}`} />
        </div>
        {model.status === 'active' ? (
          <span className="px-2 py-1 text-xs rounded-full bg-emerald-500/20 text-emerald-400 flex items-center gap-1">
            <Check className="w-3 h-3" /> Active
          </span>
        ) : (
          <span className="px-2 py-1 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
            Coming Soon
          </span>
        )}
      </div>

      <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">{model.name}</h3>
      <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">{model.description}</p>

      <div className="p-3 bg-zinc-100 dark:bg-zinc-800/50 rounded-lg mb-4">
        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Formula</p>
        <p className="text-sm font-mono text-zinc-700 dark:text-zinc-300">{model.formula}</p>
      </div>

      <div className="space-y-2 mb-4">
        {model.details.slice(0, 3).map((detail, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <ChevronRight className={`w-4 h-4 mt-0.5 ${colors.icon}`} />
            <span className="text-sm text-zinc-600 dark:text-zinc-400">{detail}</span>
          </div>
        ))}
      </div>

      <Button
        onClick={() => onSelect(model)}
        disabled={model.status !== 'active'}
        className={`w-full ${model.status === 'active' ? colors.button : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-400 cursor-not-allowed'}`}
      >
        {model.status === 'active' ? (
          <>Configure Model <ArrowRight className="w-4 h-4 ml-2" /></>
        ) : (
          'Coming Soon'
        )}
      </Button>
    </div>
  );
}

function PricingModelDetail({ model, onBack }) {
  const colors = getColorClasses(model.color);
  const Icon = model.icon;

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={onBack} className="border-zinc-300 dark:border-zinc-700">
        ← Back to Models
      </Button>

      <div className={`p-6 rounded-xl ${colors.bg} border ${colors.border}`}>
        <div className="flex items-center gap-4">
          <div className="p-4 rounded-xl bg-white dark:bg-zinc-900">
            <Icon className={`w-8 h-8 ${colors.icon}`} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-zinc-900 dark:text-white">{model.name}</h2>
            <p className="text-zinc-600 dark:text-zinc-400 mt-1">{model.usage}</p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4 flex items-center gap-2">
          <Calculator className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
          Pricing Formula
        </h3>
        <div className="p-4 bg-zinc-100 dark:bg-zinc-800 rounded-lg mb-4">
          <p className="font-mono text-lg text-emerald-600 dark:text-emerald-400">{model.formula}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {model.details.map((detail, idx) => (
            <div key={idx} className="flex items-start gap-3 p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg">
              <Check className={`w-5 h-5 mt-0.5 ${colors.icon}`} />
              <span className="text-sm text-zinc-700 dark:text-zinc-300">{detail}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-zinc-500 dark:text-zinc-400" />
          Required Input Fields
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {model.fields.map((field, idx) => (
            <div key={idx} className="p-3 bg-zinc-100 dark:bg-zinc-800 rounded-lg">
              <p className="text-sm font-medium text-zinc-900 dark:text-white">{field.name}</p>
              <p className="text-xs text-zinc-500 font-mono">{field.key}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Integration Coming Soon</p>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
              This pricing model will be integrated with Cost Templates and COGS Card creation.
              You'll be able to select this model when creating new pricing configurations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function TemplatesPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await axios.delete(`${API}/templates/${id}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openCreateDialog = () => {
    setEditingTemplate(null);
    setDialogOpen(true);
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingTemplate(null);
  };

  const handleSave = () => {
    fetchTemplates();
    closeDialog();
  };

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C3002F] mx-auto mb-4"></div>
            <p className="text-zinc-600 dark:text-zinc-400">Loading templates...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-8 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-zinc-900 dark:text-white tracking-tight">Cost Templates</h1>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1">
              Manage dynamic pricing calculation templates and pricing methodologies
            </p>
          </div>
          {activeTab === 'templates' && isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreateDialog} className="bg-[#C3002F] hover:bg-[#A3002A]">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle>
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                  </DialogTitle>
                </DialogHeader>
                <TemplateForm
                  template={editingTemplate}
                  onSave={handleSave}
                  onCancel={closeDialog}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Page Tabs */}
        <div className="border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex gap-6">
            <button
              onClick={() => { setActiveTab('templates'); setSelectedModel(null); }}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'templates'
                  ? 'border-[#C3002F] text-[#C3002F]'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
              }`}
            >
              <FileSpreadsheet className="w-4 h-4 inline mr-2" />
              Cost Templates
            </button>
            <button
              onClick={() => { setActiveTab('pricing-models'); setSelectedModel(null); }}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'pricing-models'
                  ? 'border-[#C3002F] text-[#C3002F]'
                  : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
              }`}
            >
              <Calculator className="w-4 h-4 inline mr-2" />
              Pricing Models
            </button>
          </div>
        </div>

        {/* Cost Templates Tab */}
        {activeTab === 'templates' && (
          <>
            {templates.length === 0 ? (
              <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-12 text-center">
                <FileSpreadsheet className="w-16 h-16 text-zinc-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-2">No Templates Yet</h3>
                <p className="text-zinc-600 dark:text-zinc-400 mb-4">
                  Create your first cost template with custom pricing fields
                </p>
                {isAdmin && (
                  <Button onClick={openCreateDialog} className="bg-[#C3002F] hover:bg-[#A3002A]">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isAdmin={isAdmin}
                    onEdit={() => openEditDialog(template)}
                    onDelete={() => handleDelete(template.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Pricing Models Tab */}
        {activeTab === 'pricing-models' && (
          <>
            {!selectedModel ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl">
                    <p className="text-2xl font-bold text-zinc-900 dark:text-white">{pricingModels.length}</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Total Models</p>
                  </div>
                  <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl">
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{pricingModels.filter(m => m.status === 'active').length}</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Active</p>
                  </div>
                  <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl">
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{pricingModels.filter(m => m.status === 'coming_soon').length}</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Coming Soon</p>
                  </div>
                  <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl">
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">4</p>
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">Markets Supported</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {pricingModels.map((model) => (
                    <PricingModelCard
                      key={model.id}
                      model={model}
                      onSelect={setSelectedModel}
                    />
                  ))}
                </div>
              </>
            ) : (
              <PricingModelDetail
                model={selectedModel}
                onBack={() => setSelectedModel(null)}
              />
            )}
          </>
        )}
      </div>
    </MainLayout>
  );
}

// ── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({ template, isAdmin, onEdit, onDelete }) {
  const fields = template.pricing_fields || [];
  const formulas = template.formulas || [];

  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#C3002F]/10 rounded-lg flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5 text-[#C3002F]" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900 dark:text-white">{template.name}</h3>
            {template.description && (
              <p className="text-sm text-zinc-500 mt-1">{template.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Input Variables</p>
        {fields.length === 0 ? (
          <div className="text-center py-4 text-sm text-zinc-500">No pricing fields defined</div>
        ) : (
          fields.slice(0, 4).map((field, index) => (
            <div key={index} className="flex items-center justify-between py-1.5 border-b border-zinc-200 dark:border-zinc-800 last:border-0">
              <span className="text-sm text-zinc-600 dark:text-zinc-400">{field.label}</span>
              <span className="font-mono font-semibold text-[#C3002F]">{field.percent}%</span>
            </div>
          ))
        )}
        {fields.length > 4 && (
          <p className="text-xs text-zinc-500">+{fields.length - 4} more fields</p>
        )}
      </div>

      {formulas.length > 0 && (
        <div className="mb-4 p-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg">
          <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Calculator className="w-3 h-3" /> Formulas
          </p>
          {formulas.slice(0, 3).map((formula, idx) => (
            <div key={idx} className="text-xs text-zinc-600 dark:text-zinc-400 py-1">
              <span className="text-blue-500 font-mono">{formula.key}</span>
              <span className="mx-1">=</span>
              <span className="text-zinc-500 font-mono truncate">{formula.formula?.slice(0, 20)}...</span>
            </div>
          ))}
          {formulas.length > 3 && (
            <p className="text-xs text-zinc-500">+{formulas.length - 3} more formulas</p>
          )}
        </div>
      )}

      <div className="mb-4 flex gap-2">
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
          {fields.length} {fields.length === 1 ? 'field' : 'fields'}
        </span>
        {formulas.length > 0 && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
            <Calculator className="w-3 h-3 mr-1" />
            {formulas.length} {formulas.length === 1 ? 'formula' : 'formulas'}
          </span>
        )}
      </div>

      {isAdmin && (
        <div className="flex gap-2">
          <Button onClick={onEdit} variant="outline" size="sm" className="flex-1">
            <Pencil className="w-3 h-3 mr-1" />
            Edit
          </Button>
          <Button
            onClick={onDelete}
            variant="outline"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ── Template Form ────────────────────────────────────────────────────────────

function TemplateForm({ template, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: template?.name || '',
    description: template?.description || '',
    pricing_fields: template?.pricing_fields || [],
    formulas: template?.formulas || []
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('fields');

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addField = () => {
    setForm(prev => ({
      ...prev,
      pricing_fields: [...prev.pricing_fields, { label: '', key: '', percent: 0 }]
    }));
  };

  const updateField = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      pricing_fields: prev.pricing_fields.map((f, i) =>
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const deleteField = (index) => {
    setForm(prev => ({
      ...prev,
      pricing_fields: prev.pricing_fields.filter((_, i) => i !== index)
    }));
  };

  const moveField = (index, direction) => {
    const newFields = [...form.pricing_fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newFields.length) return;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setForm(prev => ({ ...prev, pricing_fields: newFields }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    if (form.pricing_fields.length === 0) {
      toast.error('Please add at least one pricing field');
      return;
    }

    for (let field of form.pricing_fields) {
      if (!field.label.trim()) {
        toast.error('All field labels are required');
        return;
      }
    }

    const processedFields = form.pricing_fields.map(f => ({
      ...f,
      key: f.key || f.label.toLowerCase().replace(/[^a-z0-9]/g, '_')
    }));

    const processedFormulas = form.formulas.map((f, idx) => ({
      ...f,
      order: f.order ?? idx
    }));

    const payload = {
      name: form.name,
      description: form.description,
      pricing_fields: processedFields,
      formulas: processedFormulas
    };

    setSaving(true);
    try {
      if (template) {
        await axios.put(`${API}/templates/${template.id}`, payload);
        toast.success('Template updated successfully');
      } else {
        await axios.post(`${API}/templates`, payload);
        toast.success('Template created successfully');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const preventEnterSubmit = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  return (
    <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto px-1">
      <div className="space-y-4">
        <div>
          <Label>Template Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Standard Pricing Template"
            required
          />
        </div>
        <div>
          <Label>Description</Label>
          <Textarea
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Optional description"
            rows={2}
          />
        </div>
      </div>

      <div className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setActiveTab('fields')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'fields'
                ? 'border-[#C3002F] text-[#C3002F]'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
            }`}
          >
            <FileSpreadsheet className="w-4 h-4 inline mr-1" />
            Input Fields ({form.pricing_fields.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('formulas')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'formulas'
                ? 'border-[#C3002F] text-[#C3002F]'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
            }`}
          >
            <Calculator className="w-4 h-4 inline mr-1" />
            Formulas ({form.formulas.length})
          </button>
        </div>
      </div>

      {activeTab === 'fields' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-zinc-900 dark:text-white">Input Variables</h3>
            <Button
              type="button"
              onClick={addField}
              variant="outline"
              size="sm"
              className="text-[#C3002F] border-[#C3002F] hover:bg-[#C3002F] hover:text-white"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Field
            </Button>
          </div>

          {form.pricing_fields.length === 0 ? (
            <div className="text-center py-8 bg-zinc-50 dark:bg-zinc-900 rounded-lg border-2 border-dashed border-zinc-300 dark:border-zinc-700">
              <FileSpreadsheet className="w-12 h-12 text-zinc-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-zinc-900 dark:text-white mb-1">No pricing fields yet</p>
              <p className="text-xs text-zinc-500 mb-4">Add fields like ISC%, GP%, DDR%, Customs%, Freight%, etc.</p>
              <Button
                type="button"
                onClick={addField}
                variant="outline"
                size="sm"
                className="text-[#C3002F] border-[#C3002F]"
              >
                <Plus className="w-3 h-3 mr-1" />
                Add First Field
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {form.pricing_fields.map((field, index) => (
                <div key={index} className="flex gap-2 items-start p-3 bg-zinc-50 dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
                  <div className="flex flex-col gap-1 pt-2">
                    <button
                      type="button"
                      onClick={() => moveField(index, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded disabled:opacity-30"
                    >
                      <GripVertical className="w-3 h-3 text-zinc-400" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveField(index, 'down')}
                      disabled={index === form.pricing_fields.length - 1}
                      className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded disabled:opacity-30"
                    >
                      <GripVertical className="w-3 h-3 text-zinc-400 rotate-180" />
                    </button>
                  </div>

                  <div className="flex-1 space-y-2">
                    <Input
                      placeholder="Field name (e.g., ISC%, GP%, Freight%)"
                      value={field.label}
                      onChange={(e) => updateField(index, 'label', e.target.value)}
                    />
                    <div className="relative">
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="Percentage"
                        value={field.percent}
                        onChange={(e) => updateField(index, 'percent', parseFloat(e.target.value) || 0)}
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500">%</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={() => deleteField(index)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 mt-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {form.pricing_fields.length > 0 && (
            <div className="text-xs text-zinc-500 bg-blue-50 dark:bg-blue-950/20 p-3 rounded border border-blue-200 dark:border-blue-900">
              💡 <strong>Tip:</strong> Common fields include ISC% (Integrated Selling Cost), GP% (Gross Profit), DDR% (Dealer Discount Rate), Customs%, Freight%, Insurance%, etc.
            </div>
          )}
        </div>
      )}

      {activeTab === 'formulas' && (
        <div className="space-y-4">
          <div className="text-xs text-zinc-500 bg-amber-50 dark:bg-amber-950/20 p-3 rounded border border-amber-200 dark:border-amber-900 mb-4">
            💡 <strong>Formula Playground:</strong> Define Excel-like formulas to calculate COGS, DBP, MRP, etc. Use variables from Input Fields (e.g., <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">isc_percent</code>, <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">gp_percent</code>) and built-in variables (<code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">pp</code> = Purchase Price).
          </div>
          <FormulaBuilder
            formulas={form.formulas}
            onChange={(formulas) => handleChange('formulas', formulas)}
            pricingFields={form.pricing_fields}
          />
        </div>
      )}

      <div className="flex gap-3 justify-end pt-4 border-t border-zinc-200 dark:border-zinc-800 sticky bottom-0 bg-white dark:bg-zinc-950">
        <Button type="button" variant="outline" onClick={onCancel}>
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="bg-[#C3002F] hover:bg-[#A3002A]">
          <Check className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
        </Button>
      </div>
    </form>
  );
}
